import React, { useState, useCallback, useMemo, memo, useEffect, ReactNode } from 'react';

// Type definitions
interface HelmetProps {
  children: React.ReactElement;
}

interface AnalyticsEvent {
  event: string;
  properties: Record<string, any>;
  timestamp: string;
}

interface ConversionOptions {
  outputFormat: OutputFormat;
  structureMode: StructureMode;
  pageRange: string;
  includeImages: boolean;
  includeTextBoxes: boolean;
  preserveFormatting: boolean;
  extractTables: boolean;
  extractMetadata: boolean;
  flattenStructure: boolean;
  includePageInfo: boolean;
  ocrEnabled: boolean;
  ocrLanguage: string;
  removeWatermarks: boolean;
  rotationAngle: number;
  contrast: number;
  brightness: number;
  cleanupText: boolean;
  arrayFormat: ArrayFormat;
  dateFormat: DateFormat;
  numberFormat: NumberFormat;
  encoding: string;
  indentation: number;
  prettyPrint: boolean;
  escapeCharacters: boolean;
  nullHandling: NullHandling;
  booleanHandling: BooleanHandling;
  imageHandling: ImageHandling;
  qualityLevel: QualityLevel;
  compressionLevel: CompressionLevel;
}

interface ThemeContextType {
  isDark: boolean;
  toggleTheme: () => void;
}

interface AnalyticsHook {
  track: (event: string, properties?: Record<string, any>) => void;
}

interface SEOData {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  twitterCard: string;
}

interface AnalyticsStats {
  total: number;
  conversions: number;
  validations: number;
}

interface PDFInfo {
  fileName: string;
  fileSize: number;
  pageCount: number;
  version: string;
  hasImages: boolean;
  hasTables: boolean;
  isPasswordProtected: boolean;
  metadata: {
    title?: string;
    author?: string;
    subject?: string;
    creator?: string;
    producer?: string;
    creationDate?: string;
    modificationDate?: string;
  };
}

// Output format options
type OutputFormat = 'json' | 'jsonl' | 'json-compact' | 'json-pretty' | 'json-schema';
type StructureMode = 'automatic' | 'page-based' | 'table-based' | 'text-blocks' | 'hierarchical' | 'flat';
type ArrayFormat = 'nested' | 'flat' | 'indexed';
type DateFormat = 'iso' | 'timestamp' | 'unix' | 'custom' | 'preserve';
type NumberFormat = 'auto' | 'string' | 'number' | 'preserve';
type NullHandling = 'null' | 'empty-string' | 'omit' | 'zero';
type BooleanHandling = 'boolean' | 'string' | 'number';
type ImageHandling = 'ignore' | 'base64' | 'url' | 'metadata';
type QualityLevel = 'fast' | 'balanced' | 'high' | 'ultra';
type CompressionLevel = 'none' | 'low' | 'medium' | 'high' | 'maximum';

// Mock react-helmet-async for SEO
const Helmet: React.FC<HelmetProps> = ({ children }) => {
  useEffect(() => {
    const extractPropsFromChildren = (element: React.ReactElement): any => {
      if (element.props?.children) {
        const childArray = React.Children.toArray(element.props.children);
        const helmetData: any = { meta: [], link: [] };

        childArray.forEach((child: any) => {
          if (React.isValidElement(child)) {
            const childElement = child as React.ReactElement<any>;

            if (childElement.type === 'title') {
              helmetData.title = childElement.props.children;
            } else if (childElement.type === 'meta') {
              helmetData.meta.push(childElement.props);
            } else if (childElement.type === 'link') {
              helmetData.link.push(childElement.props);
            } else if (childElement.type === 'script') {
              if (childElement.props.type === 'application/ld+json') {
                const script = document.createElement('script');
                script.type = 'application/ld+json';
                script.textContent = childElement.props.children;
                document.head.appendChild(script);
              }
            }
          }
        });
        return helmetData;
      }
      return {};
    };

    const helmetData = extractPropsFromChildren(children);

    if (helmetData.title) {
      document.title = helmetData.title;
    }

    const updateMeta = (name: string, content: string): void => {
      let meta = document.querySelector(`meta[name="${name}"]`) ||
        document.querySelector(`meta[property="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        if (name.startsWith('og:') || name.startsWith('twitter:')) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    if (helmetData.meta && Array.isArray(helmetData.meta)) {
      helmetData.meta.forEach((meta: any) => {
        if (meta.name) updateMeta(meta.name, meta.content);
        if (meta.property) updateMeta(meta.property, meta.content);
      });
    }

    if (helmetData.link && Array.isArray(helmetData.link)) {
      helmetData.link.forEach((link: any) => {
        let linkEl = document.querySelector(`link[rel="${link.rel}"]`) as HTMLLinkElement;
        if (!linkEl) {
          linkEl = document.createElement('link') as HTMLLinkElement;
          linkEl.rel = link.rel;
          document.head.appendChild(linkEl);
        }
        Object.keys(link).forEach((attr) => {
          if (attr !== 'rel') linkEl.setAttribute(attr, link[attr]);
        });
      });
    }
  }, [children]);

  return null;
};

// Analytics hook with in-memory storage
const useAnalytics = (): AnalyticsHook => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsEvent[]>([]);

  const track = useCallback((event: string, properties: Record<string, any> = {}): void => {
    console.log('Analytics Event:', event, properties);

    const analyticsEvent: AnalyticsEvent = {
      event,
      properties,
      timestamp: new Date().toISOString()
    };

    setAnalyticsData(prev => [...prev.slice(-99), analyticsEvent]);

    // Also track with gtag if available
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', event, properties);
    }
  }, []);

  return { track };
};

// Theme context
const ThemeContext = React.createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [isDark, setIsDark] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const stored = sessionStorage.getItem('theme');
      return stored === 'dark' ||
        (!stored && window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
    return false;
  });

  const toggleTheme = useCallback((): void => {
    setIsDark((prev) => {
      const newTheme = !prev;
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('theme', newTheme ? 'dark' : 'light');
      }
      return newTheme;
    });
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark);
  }, [isDark]);

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

const useTheme = (): ThemeContextType => {
  const context = React.useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Mock PDF analysis function
const analyzePDFFile = async (file: File): Promise<PDFInfo> => {
  // Simulate PDF analysis
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  return {
    fileName: file.name,
    fileSize: file.size,
    pageCount: Math.floor(Math.random() * 50) + 1,
    version: '1.4',
    hasImages: Math.random() > 0.5,
    hasTables: Math.random() > 0.3,
    isPasswordProtected: Math.random() > 0.8,
    metadata: {
      title: 'Sample PDF Document',
      author: 'Document Author',
      subject: 'PDF Analysis',
      creator: 'PDF Creator',
      producer: 'PDF Producer',
      creationDate: '2024-01-01',
      modificationDate: new Date().toISOString().split('T')[0]
    }
  };
};

// Mock PDF to JSON conversion
const convertPDFToJSON = async (file: File, options: ConversionOptions): Promise<Blob> => {
  // Simulate conversion process
  await new Promise(resolve => setTimeout(resolve, 3000));
  
  // Create mock JSON data based on structure mode
  let mockData: any;
  
  switch (options.structureMode) {
    case 'page-based':
      mockData = {
        document: {
          metadata: {
            title: "Sample PDF Document",
            author: "Document Author",
            pageCount: 3,
            creationDate: "2024-01-01T00:00:00Z"
          },
          pages: [
            {
              pageNumber: 1,
              content: [
                {
                  type: "heading",
                  text: "Financial Report Q4 2024",
                  position: { x: 100, y: 50 }
                },
                {
                  type: "table",
                  data: [
                    { "Product": "Widget A", "Sales": 1500, "Revenue": 75000 },
                    { "Product": "Widget B", "Sales": 1200, "Revenue": 60000 },
                    { "Product": "Widget C", "Sales": 800, "Revenue": 40000 }
                  ],
                  position: { x: 50, y: 100 }
                }
              ]
            },
            {
              pageNumber: 2,
              content: [
                {
                  type: "paragraph",
                  text: "This document contains important financial information for the fourth quarter of 2024.",
                  position: { x: 50, y: 50 }
                },
                {
                  type: "list",
                  items: [
                    "Total revenue increased by 15%",
                    "Operating expenses decreased by 8%",
                    "Net profit margin improved to 22%"
                  ],
                  position: { x: 50, y: 100 }
                }
              ]
            }
          ]
        }
      };
      break;
      
    case 'table-based':
      mockData = {
        tables: [
          {
            tableId: 1,
            page: 1,
            title: "Sales Data Q4 2024",
            headers: ["Product", "Sales", "Revenue"],
            rows: [
              ["Widget A", 1500, 75000],
              ["Widget B", 1200, 60000],
              ["Widget C", 800, 40000]
            ],
            position: { x: 50, y: 100, width: 400, height: 120 }
          },
          {
            tableId: 2,
            page: 2,
            title: "Expense Breakdown",
            headers: ["Category", "Amount", "Percentage"],
            rows: [
              ["Marketing", 25000, "15%"],
              ["Operations", 40000, "24%"],
              ["Personnel", 80000, "48%"],
              ["Other", 22000, "13%"]
            ],
            position: { x: 50, y: 200, width: 350, height: 150 }
          }
        ],
        summary: {
          totalTables: 2,
          totalRows: 7,
          extractedData: "Sales and expense data successfully extracted"
        }
      };
      break;
      
    case 'flat':
      mockData = [
        { type: "title", content: "Financial Report Q4 2024", page: 1 },
        { type: "data", product: "Widget A", sales: 1500, revenue: 75000, page: 1 },
        { type: "data", product: "Widget B", sales: 1200, revenue: 60000, page: 1 },
        { type: "data", product: "Widget C", sales: 800, revenue: 40000, page: 1 },
        { type: "text", content: "This document contains important financial information", page: 2 },
        { type: "data", category: "Marketing", amount: 25000, percentage: "15%", page: 2 },
        { type: "data", category: "Operations", amount: 40000, percentage: "24%", page: 2 }
      ];
      break;
      
    default:
      mockData = {
        document: {
          info: {
            title: "Sample PDF Document",
            pages: 3,
            extractionDate: new Date().toISOString()
          },
          content: {
            text: "Financial Report Q4 2024\n\nThis document contains important financial information...",
            tables: [
              {
                name: "Sales Data",
                data: [
                  { "Product": "Widget A", "Sales": 1500, "Revenue": 75000 },
                  { "Product": "Widget B", "Sales": 1200, "Revenue": 60000 },
                  { "Product": "Widget C", "Sales": 800, "Revenue": 40000 }
                ]
              }
            ]
          }
        }
      };
  }

  // Apply formatting options
  let jsonString: string;
  if (options.prettyPrint) {
    jsonString = JSON.stringify(mockData, null, options.indentation);
  } else {
    jsonString = JSON.stringify(mockData);
  }

  // Handle different output formats
  if (options.outputFormat === 'jsonl') {
    // Convert to JSON Lines format
    if (Array.isArray(mockData)) {
      jsonString = mockData.map(item => JSON.stringify(item)).join('\n');
    } else {
      jsonString = JSON.stringify(mockData);
    }
  }

  return new Blob([jsonString], { type: 'application/json' });
};

// Analytics Dashboard Component
const AnalyticsDashboard: React.FC = memo(() => {
  const { isDark } = useTheme();

  const stats: AnalyticsStats = useMemo(() => {
    // Mock analytics data since we don't have persistent storage
    const total = Math.floor(Math.random() * 100) + 50;
    const conversions = Math.floor(Math.random() * 50) + 20;
    const validations = Math.floor(Math.random() * 30) + 10;

    return { total, conversions, validations };
  }, []);

  return (
    <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
      <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
        Usage Analytics
      </h3>
      <div className="grid grid-cols-4 gap-4">
        <div className={`text-center p-3 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
          <div className={`text-2xl font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
            {Math.floor(Math.random() * 100) + 50}
          </div>
          <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Total Files
          </div>
        </div>
        <div className={`text-center p-3 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
          <div className={`text-2xl font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
            {Math.floor(Math.random() * 50) + 20}
          </div>
          <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Conversions
          </div>
        </div>
        <div className={`text-center p-3 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
          <div className={`text-2xl font-bold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
            {Math.floor(Math.random() * 30) + 10}
          </div>
          <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Success Rate
          </div>
        </div>
        <div className={`text-center p-3 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
          <div className={`text-2xl font-bold ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>
            {Math.floor(Math.random() * 1000) + 500}MB
          </div>
          <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Data Processed
          </div>
        </div>
      </div>
    </div>
  );
});

AnalyticsDashboard.displayName = 'AnalyticsDashboard';

// PDF Preview Component
const PDFPreview: React.FC<{ pdfInfo: PDFInfo | null; isDark: boolean }> = memo(({ pdfInfo, isDark }) => {
  if (!pdfInfo) return null;

  return (
    <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
      <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
        PDF Analysis
      </h3>
      
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>File Name:</span>
            <p className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{pdfInfo.fileName}</p>
          </div>
          <div>
            <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>File Size:</span>
            <p className={`${isDark ? 'text-white' : 'text-gray-900'}`}>
              {(pdfInfo.fileSize / 1024 / 1024).toFixed(2)} MB
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Pages:</span>
            <p className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{pdfInfo.pageCount}</p>
          </div>
          <div>
            <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>PDF Version:</span>
            <p className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{pdfInfo.version}</p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="flex items-center">
            <div className={`w-3 h-3 rounded-full mr-2 ${pdfInfo.hasTables ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {pdfInfo.hasTables ? 'Has Tables' : 'No Tables'}
            </span>
          </div>
          <div className="flex items-center">
            <div className={`w-3 h-3 rounded-full mr-2 ${pdfInfo.hasImages ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {pdfInfo.hasImages ? 'Has Images' : 'No Images'}
            </span>
          </div>
          <div className="flex items-center">
            <div className={`w-3 h-3 rounded-full mr-2 ${pdfInfo.isPasswordProtected ? 'bg-orange-500' : 'bg-green-500'}`}></div>
            <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {pdfInfo.isPasswordProtected ? 'Protected' : 'Open'}
            </span>
          </div>
        </div>

        {pdfInfo.metadata && (
          <div className={`mt-4 p-3 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
            <h4 className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Metadata</h4>
            <div className="space-y-1 text-sm">
              {pdfInfo.metadata.title && (
                <div>
                  <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Title: </span>
                  <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{pdfInfo.metadata.title}</span>
                </div>
              )}
              {pdfInfo.metadata.author && (
                <div>
                  <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Author: </span>
                  <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{pdfInfo.metadata.author}</span>
                </div>
              )}
              {pdfInfo.metadata.creationDate && (
                <div>
                  <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Created: </span>
                  <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{pdfInfo.metadata.creationDate}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

PDFPreview.displayName = 'PDFPreview';
// const [isExpanded, setIsExpanded] = useState(false);
const isExpanded = false;
// JSON Preview Component
const JSONPreview: React.FC<{ jsonData: string | null; isDark: boolean }> = memo(({ jsonData, isDark }) => {
  if (!jsonData) return null;

  
  const maxPreviewLength = 500;
  const isLong = jsonData.length > maxPreviewLength;
  const displayData = isExpanded ? jsonData : jsonData.slice(0, maxPreviewLength);

  return (
    <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
      <div className="flex justify-between items-center mb-3">
        <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          JSON Preview
        </h3>
        <div className="flex space-x-2">
          <button
            onClick={() => navigator.clipboard.writeText(jsonData)}
            className={`px-3 py-1 text-sm rounded transition-colors ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
            type="button"
          >
            📋 Copy
          </button>
          {isLong && (
            <button
            //   onClick={() => setIsExpanded(!isExpanded)}
              className={`px-3 py-1 text-sm rounded transition-colors ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
              type="button"
            >
              {isExpanded ? 'Collapse' : 'Expand'}
            </button>
          )}
        </div>
      </div>
      
      <div className={`relative rounded-lg p-3 ${isDark ? 'bg-gray-900' : 'bg-white'} border ${isDark ? 'border-gray-600' : 'border-gray-200'}`}>
        <pre className={`text-sm overflow-x-auto ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
          <code>{displayData}</code>
          {isLong && !isExpanded && (
            <span className={`${isDark ? 'text-gray-500' : 'text-gray-400'}`}>...</span>
          )}
        </pre>
      </div>
      
      {isLong && (
        <p className={`text-xs mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          {jsonData.length} characters • {jsonData.split('\n').length} lines
        </p>
      )}
    </div>
  );
});

JSONPreview.displayName = 'JSONPreview';

// Main Component Content
const PDFToJSONContent: React.FC = () => {
  const { isDark, toggleTheme } = useTheme();
  const { track } = useAnalytics();

  // States
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [pdfInfo, setPdfInfo] = useState<PDFInfo | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);
  const [isConverting, setIsConverting] = useState<boolean>(false);
  const [conversionProgress, setConversionProgress] = useState<number>(0);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [fileName, setFileName] = useState<string>('converted_data');
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [dragActive, setDragActive] = useState<boolean>(false);
  const [jsonPreview, setJsonPreview] = useState<string | null>(null);

  // Advanced options
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('json-pretty');
  const [structureMode, setStructureMode] = useState<StructureMode>('automatic');
  const [pageRange, setPageRange] = useState<string>('all');
  const [includeImages, setIncludeImages] = useState<boolean>(false);
  const [includeTextBoxes, setIncludeTextBoxes] = useState<boolean>(true);
  const [preserveFormatting, setPreserveFormatting] = useState<boolean>(true);
  const [extractTables, setExtractTables] = useState<boolean>(true);
  const [extractMetadata, setExtractMetadata] = useState<boolean>(true);
  const [flattenStructure, setFlattenStructure] = useState<boolean>(false);
  const [includePageInfo, setIncludePageInfo] = useState<boolean>(true);
  const [ocrEnabled, setOcrEnabled] = useState<boolean>(false);
  const [ocrLanguage, setOcrLanguage] = useState<string>('eng');
  const [removeWatermarks, setRemoveWatermarks] = useState<boolean>(false);
  const [rotationAngle, setRotationAngle] = useState<number>(0);
  const [contrast, setContrast] = useState<number>(100);
  const [brightness, setBrightness] = useState<number>(100);
  const [cleanupText, setCleanupText] = useState<boolean>(true);
  const [arrayFormat, setArrayFormat] = useState<ArrayFormat>('nested');
  const [dateFormat, setDateFormat] = useState<DateFormat>('iso');
  const [numberFormat, setNumberFormat] = useState<NumberFormat>('auto');
  const [encoding, setEncoding] = useState<string>('UTF-8');
  const [indentation, setIndentation] = useState<number>(2);
  const [prettyPrint, setPrettyPrint] = useState<boolean>(true);
  const [escapeCharacters, setEscapeCharacters] = useState<boolean>(true);
  const [nullHandling, setNullHandling] = useState<NullHandling>('null');
  const [booleanHandling, setBooleanHandling] = useState<BooleanHandling>('boolean');
  const [imageHandling, setImageHandling] = useState<ImageHandling>('metadata');
  const [qualityLevel, setQualityLevel] = useState<QualityLevel>('balanced');
  const [compressionLevel, setCompressionLevel] = useState<CompressionLevel>('medium');

  // SEO data
  const seoData: SEOData = useMemo(() => ({
    title: "Free PDF to JSON Converter Online - Extract PDF Data to JSON Format",
    description: "Convert PDF files to JSON format online for free. Advanced features include table extraction, OCR, metadata extraction, and multiple JSON structure modes. Perfect for data analysis and API integration.",
    keywords: "PDF to JSON converter, PDF converter, JSON converter, online converter, free PDF converter, PDF parser, JSON formatter, data extraction, API integration",
    canonical: "https://yoursite.com/pdf-to-json-converter",
    ogTitle: "PDF to JSON Converter - Free Online Tool",
    ogDescription: "Convert your PDF files to JSON format instantly. Supports table extraction, OCR, and advanced formatting options for perfect data extraction.",
    ogImage: "https://yoursite.com/og-pdf-json-converter.jpg",
    twitterCard: "summary_large_image"
  }), []);

  // Handle file selection
  const handleFileSelect = useCallback(async (file: File): Promise<void> => {
    if (!file.type.includes('pdf')) {
      setError('Please select a valid PDF file');
      return;
    }

    if (file.size > 100 * 1024 * 1024) { // 100MB limit
      setError('File size must be less than 100MB');
      return;
    }

    setSelectedFile(file);
    setError('');
    setSuccess('');
    setPdfInfo(null);
    setJsonPreview(null);
    
    // Set default filename
    const nameParts = file.name.split('.');
    if (nameParts.length > 1) {
      nameParts.pop();
      setFileName(nameParts.join('.'));
    } else {
      setFileName(file.name);
    }

    track('pdf_file_selected', {
      fileName: file.name,
      fileSize: file.size
    });

    // Analyze PDF
    setIsAnalyzing(true);
    try {
      const info = await analyzePDFFile(file);
      setPdfInfo(info);
      track('pdf_analyzed', {
        pageCount: info.pageCount,
        hasImages: info.hasImages,
        hasTables: info.hasTables,
        isPasswordProtected: info.isPasswordProtected
      });
    } catch (err) {
      setError(`Failed to analyze PDF: ${err instanceof Error ? err.message : String(err)}`);
      track('pdf_analysis_error', { error: err instanceof Error ? err.message : String(err) });
    } finally {
      setIsAnalyzing(false);
    }
  }, [track]);

  // Handle file upload
  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  // Handle drag and drop
  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragActive(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setDragActive(false);
  }, []);

  // Convert PDF to JSON
  const convertToJSON = useCallback(async (): Promise<void> => {
    if (!selectedFile) {
      setError('Please select a PDF file first');
      return;
    }

    setError('');
    setSuccess('');
    setIsConverting(true);
    setConversionProgress(0);

    const options: ConversionOptions = {
      outputFormat,
      structureMode,
      pageRange,
      includeImages,
      includeTextBoxes,
      preserveFormatting,
      extractTables,
      extractMetadata,
      flattenStructure,
      includePageInfo,
      ocrEnabled,
      ocrLanguage,
      removeWatermarks,
      rotationAngle,
      contrast,
      brightness,
      cleanupText,
      arrayFormat,
      dateFormat,
      numberFormat,
      encoding,
      indentation,
      prettyPrint,
      escapeCharacters,
      nullHandling,
      booleanHandling,
      imageHandling,
      qualityLevel,
      compressionLevel
    };

    try {
      const startTime = Date.now();

      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setConversionProgress(prev => {
          if (prev >= 90) return prev;
          return prev + Math.random() * 10;
        });
      }, 200);

      const resultBlob = await convertPDFToJSON(selectedFile, options);
      
      clearInterval(progressInterval);
      setConversionProgress(100);

      // Read the blob content for preview
      const jsonText = await resultBlob.text();
      setJsonPreview(jsonText);

      // Download the result
      const url = URL.createObjectURL(resultBlob);
      const link = document.createElement('a');
      link.href = url;
      
      const extension = outputFormat === 'jsonl' ? 'jsonl' : 'json';
      link.download = `${fileName || 'converted_data'}.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      const processingTime = Date.now() - startTime;
      setSuccess('PDF successfully converted to JSON! Download started automatically.');
      
      track('pdf_converted', {
        format: outputFormat,
        processingTime,
        fileSize: selectedFile.size,
        pageCount: pdfInfo?.pageCount,
        options: {
          structureMode,
          qualityLevel,
          ocrEnabled,
          extractTables
        }
      });

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(`Conversion failed: ${errorMessage}`);
      track('conversion_error', { error: errorMessage });
    } finally {
      setIsConverting(false);
      setConversionProgress(0);
    }
  }, [selectedFile, fileName, outputFormat, structureMode, pageRange, includeImages, includeTextBoxes, preserveFormatting, extractTables, extractMetadata, flattenStructure, includePageInfo, ocrEnabled, ocrLanguage, removeWatermarks, rotationAngle, contrast, brightness, cleanupText, arrayFormat, dateFormat, numberFormat, encoding, indentation, prettyPrint, escapeCharacters, nullHandling, booleanHandling, imageHandling, qualityLevel, compressionLevel, pdfInfo?.pageCount, track]);

  // Clear all
  const clearAll = useCallback((): void => {
    setSelectedFile(null);
    setPdfInfo(null);
    setError('');
    setSuccess('');
    setFileName('converted_data');
    setConversionProgress(0);
    setJsonPreview(null);
    track('data_cleared');
  }, [track]);

  // Toggle fullscreen
  const toggleFullscreen = useCallback((): void => {
    setIsFullscreen(!isFullscreen);
    track('fullscreen_toggled', { isFullscreen: !isFullscreen });
  }, [isFullscreen, track]);

  return (
    <div className={`min-h-screen transition-colors duration-200 ${isDark ? 'bg-gray-900' : 'bg-gray-50'} ${isFullscreen ? 'fixed inset-0 z-50 overflow-auto' : ''}`}>

      {/* SEO Meta Tags */}
      <Helmet>
        <>
          <title>{seoData.title}</title>
          <meta name="description" content={seoData.description} />
          <meta name="keywords" content={seoData.keywords} />
          <link rel="canonical" href={seoData.canonical} />

          <meta property="og:title" content={seoData.ogTitle} />
          <meta property="og:description" content={seoData.ogDescription} />
          <meta property="og:image" content={seoData.ogImage} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={seoData.canonical} />

          <meta name="twitter:card" content={seoData.twitterCard} />
          <meta name="twitter:title" content={seoData.ogTitle} />
          <meta name="twitter:description" content={seoData.ogDescription} />
          <meta name="twitter:image" content={seoData.ogImage} />

          <meta name="robots" content="index, follow" />
          <meta name="author" content="PDF to JSON Converter Tool" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />

          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebApplication",
              "name": "PDF to JSON Converter",
              "description": seoData.description,
              "url": seoData.canonical,
              "applicationCategory": "UtilityApplication",
              "operatingSystem": "Any",
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD"
              }
            })}
          </script>
        </>
      </Helmet>

      <div className={`${isFullscreen ? 'p-4' : 'max-w-7xl mx-auto p-6'}`}>

        {/* Header with Controls */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              PDF to JSON Converter
            </h1>
            <p className={`mt-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Convert PDF files to JSON format with advanced data extraction, table recognition, and structure analysis
            </p>
          </div>

          <div className="flex space-x-2">
            {/* Analytics Toggle */}
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-white hover:bg-gray-50 text-gray-900'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
              title="Toggle Analytics"
              type="button"
            >
              📊
            </button>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-white hover:bg-gray-50 text-gray-900'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
              title="Toggle Theme"
              type="button"
            >
              {isDark ? '☀️' : '🌙'}
            </button>

            {/* Fullscreen Toggle */}
            <button
              onClick={toggleFullscreen}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-white hover:bg-gray-50 text-gray-900'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
              title="Toggle Fullscreen"
              type="button"
            >
              {isFullscreen ? '📱' : '🖥️'}
            </button>
          </div>
        </div>

        {/* Analytics Dashboard */}
        {showAnalytics && (
          <div className="mb-6">
            <AnalyticsDashboard />
          </div>
        )}

        {/* Description */}
        <div className={`mb-6 p-4 rounded-lg border ${isDark ? 'bg-blue-900/20 border-blue-800 text-blue-200' : 'bg-blue-50 border-blue-100 text-blue-800'}`}>
          <p>
            Convert PDF documents to JSON format with intelligent data extraction, table recognition, OCR support, and flexible structure options.
            Perfect for API integration, data analysis, and automated document processing workflows.
          </p>
        </div>

        <div className={`grid ${isFullscreen ? 'grid-cols-1 lg:grid-cols-3' : 'grid-cols-1 md:grid-cols-3'} gap-6`}>

          {/* File Upload Section */}
          <div className="flex flex-col h-full">
            <div className={`p-4 rounded-lg shadow-sm mb-4 flex-grow border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="flex justify-between items-center mb-3">
                <label className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  PDF File Upload
                </label>
                {selectedFile && (
                  <button
                    className={`text-sm transition-colors ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-800'}`}
                    onClick={clearAll}
                    type="button"
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* Drag and Drop Area */}
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActive
                    ? isDark ? 'border-blue-400 bg-blue-900/20' : 'border-blue-400 bg-blue-50'
                    : isDark ? 'border-gray-600 hover:border-gray-500' : 'border-gray-300 hover:border-gray-400'
                }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <div className="space-y-4">
                  <div className={`text-4xl ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    📄
                  </div>
                  <div>
                    <p className={`text-lg font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                      Drop your PDF file here
                    </p>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      or click to browse
                    </p>
                  </div>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="pdf-upload"
                  />
                  <label
                    htmlFor="pdf-upload"
                    className={`inline-block px-4 py-2 rounded-lg font-medium cursor-pointer transition-colors ${
                      isDark
                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    Select PDF File
                  </label>
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Maximum file size: 100MB
                  </p>
                </div>
              </div>

              {/* File Info */}
              {selectedFile && (
                <div className={`mt-4 p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <h4 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Selected File</h4>
                  <div className="space-y-1 text-sm">
                    <div>
                      <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Name: </span>
                      <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedFile.name}</span>
                    </div>
                    <div>
                      <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Size: </span>
                      <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                    </div>
                    <div>
                      <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Type: </span>
                      <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedFile.type}</span>
                    </div>
                  </div>
                  
                  {isAnalyzing && (
                    <div className="mt-3 flex items-center">
                      <svg className="animate-spin h-4 w-4 mr-2 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Analyzing PDF...</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* PDF Preview */}
            {pdfInfo && <PDFPreview pdfInfo={pdfInfo} isDark={isDark} />}
          </div>

          {/* Configuration Section */}
          <div className={`p-4 rounded-lg shadow-sm border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Conversion Settings
            </h3>

            <div className="space-y-4">
              {/* Basic Settings */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  Output File Name
                </label>
                <input
                  type="text"
                  className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                  value={fileName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFileName(e.target.value)}
                  placeholder="Enter file name (without extension)"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  Output Format
                </label>
                <select
                  className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                  value={outputFormat}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setOutputFormat(e.target.value as OutputFormat)}
                >
                  <option value="json-pretty">JSON (Pretty Formatted)</option>
                  <option value="json-compact">JSON (Compact)</option>
                  <option value="jsonl">JSON Lines (.jsonl)</option>
                  <option value="json-schema">JSON with Schema</option>
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  Structure Mode
                </label>
                <select
                  className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                  value={structureMode}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setStructureMode(e.target.value as StructureMode)}
                >
                  <option value="automatic">Automatic Detection</option>
                  <option value="page-based">Page-Based Structure</option>
                  <option value="table-based">Table-Based Extraction</option>
                  <option value="text-blocks">Text Block Analysis</option>
                  <option value="hierarchical">Hierarchical Structure</option>
                  <option value="flat">Flat Array Format</option>
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  Page Range
                </label>
                <input
                  type="text"
                  className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                  value={pageRange}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPageRange(e.target.value)}
                  placeholder="e.g., 1-5, 8, 10-12 or 'all'"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  Quality Level
                </label>
                <select
                  className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                  value={qualityLevel}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setQualityLevel(e.target.value as QualityLevel)}
                >
                  <option value="fast">Fast (Quick processing)</option>
                  <option value="balanced">Balanced (Recommended)</option>
                  <option value="high">High (Better accuracy)</option>
                  <option value="ultra">Ultra (Maximum quality)</option>
                </select>
              </div>

              {/* Quick Options */}
              <div className="space-y-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="extractTables"
                    checked={extractTables}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setExtractTables(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="extractTables" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                    Extract tables as structured data
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="extractMetadata"
                    checked={extractMetadata}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setExtractMetadata(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="extractMetadata" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                    Include document metadata
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="includePageInfo"
                    checked={includePageInfo}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIncludePageInfo(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="includePageInfo" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                    Include page information
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="ocrEnabled"
                    checked={ocrEnabled}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOcrEnabled(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="ocrEnabled" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                    Enable OCR for scanned PDFs
                  </label>
                </div>
              </div>

              {/* Advanced Options Toggle */}
              <div className="mb-4">
                <button
                  className={`text-sm flex items-center transition-colors ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  type="button"
                >
                  <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              {/* Advanced Settings */}
              {showAdvanced && (
                <div className={`space-y-4 p-3 rounded-lg mb-4 ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  
                  {/* JSON Formatting */}
                  <div className="space-y-3">
                    <h4 className={`font-medium text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>JSON Formatting</h4>

                    <div>
                      <label htmlFor="escapeCharacters" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Escape special characters
                      </label>
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Array Format
                      </label>
                      <select
                        className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                        value={arrayFormat}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setArrayFormat(e.target.value as ArrayFormat)}
                      >
                        <option value="nested">Nested objects</option>
                        <option value="flat">Flat structure</option>
                        <option value="indexed">Indexed arrays</option>
                      </select>
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Null Handling
                      </label>
                      <select
                        className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                        value={nullHandling}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setNullHandling(e.target.value as NullHandling)}
                      >
                        <option value="null">Use null values</option>
                        <option value="empty-string">Use empty strings</option>
                        <option value="omit">Omit null fields</option>
                        <option value="zero">Use zero values</option>
                      </select>
                    </div>
                  </div>

                  {/* Data Types */}
                  <div className="space-y-3">
                    <h4 className={`font-medium text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Data Types</h4>

                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Date Format
                      </label>
                      <select
                        className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                        value={dateFormat}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setDateFormat(e.target.value as DateFormat)}
                      >
                        <option value="iso">ISO 8601 (2024-01-01T00:00:00Z)</option>
                        <option value="timestamp">Timestamp (1704067200000)</option>
                        <option value="unix">Unix time (1704067200)</option>
                        <option value="preserve">Preserve original format</option>
                        <option value="custom">Custom format</option>
                      </select>
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Number Format
                      </label>
                      <select
                        className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                        value={numberFormat}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setNumberFormat(e.target.value as NumberFormat)}
                      >
                        <option value="auto">Auto-detect type</option>
                        <option value="number">Always number type</option>
                        <option value="string">Always string type</option>
                        <option value="preserve">Preserve as found</option>
                      </select>
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Boolean Handling
                      </label>
                      <select
                        className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                        value={booleanHandling}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setBooleanHandling(e.target.value as BooleanHandling)}
                      >
                        <option value="boolean">Boolean (true/false)</option>
                        <option value="string">String ("true"/"false")</option>
                        <option value="number">Number (1/0)</option>
                      </select>
                    </div>
                  </div>

                  {/* Content Options */}
                  <div className="space-y-3">
                    <h4 className={`font-medium text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Content Processing</h4>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="preserveFormatting"
                        checked={preserveFormatting}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPreserveFormatting(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="preserveFormatting" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Preserve text formatting information
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="includeTextBoxes"
                        checked={includeTextBoxes}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIncludeTextBoxes(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="includeTextBoxes" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Include text boxes and annotations
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="flattenStructure"
                        checked={flattenStructure}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFlattenStructure(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="flattenStructure" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Flatten nested structures
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="cleanupText"
                        checked={cleanupText}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCleanupText(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="cleanupText" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Clean up extracted text
                      </label>
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Image Handling
                      </label>
                      <select
                        className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                        value={imageHandling}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setImageHandling(e.target.value as ImageHandling)}
                      >
                        <option value="ignore">Ignore images</option>
                        <option value="metadata">Include metadata only</option>
                        <option value="base64">Embed as base64</option>
                        <option value="url">Generate URLs</option>
                      </select>
                    </div>
                  </div>

                  {/* OCR Settings */}
                  {ocrEnabled && (
                    <div className="space-y-3">
                      <h4 className={`font-medium text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>OCR Settings</h4>

                      <div>
                        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          OCR Language
                        </label>
                        <select
                          className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                          value={ocrLanguage}
                          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setOcrLanguage(e.target.value)}
                        >
                          <option value="eng">English</option>
                          <option value="spa">Spanish</option>
                          <option value="fra">French</option>
                          <option value="deu">German</option>
                          <option value="ita">Italian</option>
                          <option value="por">Portuguese</option>
                          <option value="rus">Russian</option>
                          <option value="chi_sim">Chinese (Simplified)</option>
                          <option value="chi_tra">Chinese (Traditional)</option>
                          <option value="jpn">Japanese</option>
                          <option value="kor">Korean</option>
                          <option value="ara">Arabic</option>
                          <option value="hin">Hindi</option>
                        </select>
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="removeWatermarks"
                          checked={removeWatermarks}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRemoveWatermarks(e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="removeWatermarks" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          Attempt to remove watermarks
                        </label>
                      </div>

                      <div>
                        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          Image Brightness: {brightness}%
                        </label>
                        <input
                          type="range"
                          min="50"
                          max="150"
                          value={brightness}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBrightness(parseInt(e.target.value))}
                          className="w-full"
                        />
                      </div>

                      <div>
                        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          Image Contrast: {contrast}%
                        </label>
                        <input
                          type="range"
                          min="50"
                          max="150"
                          value={contrast}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setContrast(parseInt(e.target.value))}
                          className="w-full"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Convert Button */}
              <div className="mt-6">
                <button
                  className={`w-full px-4 py-3 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    selectedFile && !isConverting
                      ? isDark
                        ? 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500'
                        : 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500'
                      : isDark
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                  onClick={convertToJSON}
                  disabled={!selectedFile || isConverting}
                  type="button"
                >
                  {isConverting ? (
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Converting... {Math.round(conversionProgress)}%
                    </div>
                  ) : (
                    `Convert to JSON`
                  )}
                </button>

                {isConverting && (
                  <div className="mt-2">
                    <div className={`w-full bg-gray-200 rounded-full h-2 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${conversionProgress}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className={`p-4 rounded-lg shadow-sm border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Conversion Results
            </h3>

            {/* Status Messages */}
            {error && (
              <div className={`border p-3 rounded-lg mb-4 ${isDark ? 'bg-red-900/20 border-red-800 text-red-200' : 'bg-red-50 border-red-200 text-red-700'}`}>
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}

            {success && (
              <div className={`border p-3 rounded-lg mb-4 ${isDark ? 'bg-green-900/20 border-green-800 text-green-200' : 'bg-green-50 border-green-200 text-green-700'}`}>
                <div className="flex items-center mb-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="font-medium">Success!</p>
                </div>
                <p>{success}</p>
              </div>
            )}

            {!selectedFile && !error && !success && (
              <div className={`border p-8 rounded-lg flex items-center justify-center ${isDark ? 'bg-gray-700 border-gray-600 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p>
                    Upload a PDF file to start conversion
                  </p>
                </div>
              </div>
            )}

            {selectedFile && !error && !success && !isConverting && (
              <div className={`border p-6 rounded-lg ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                <div className="text-center">
                  <div className={`text-4xl mb-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    🔄
                  </div>
                  <p className={`text-lg font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                    Ready to Convert
                  </p>
                  <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Click the convert button to transform your PDF into JSON format
                  </p>
                </div>
              </div>
            )}

            {/* JSON Preview */}
            {jsonPreview && (
              <div className="mb-4">
                <JSONPreview jsonData={jsonPreview} isDark={isDark} />
              </div>
            )}

            {/* Conversion Tips */}
            <div className={`mt-6 p-4 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
              <h4 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>💡 Conversion Tips</h4>
              <ul className={`text-sm space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li>• Use "Page-Based" structure for document-centric JSON output</li>
                <li>• Choose "Table-Based" mode for extracting tabular data</li>
                <li>• Enable OCR for scanned documents or image-based PDFs</li>
                <li>• Use "Flat Array" structure for simple data processing</li>
                <li>• Include metadata for comprehensive document information</li>
              </ul>
            </div>

            {/* Quick Actions */}
            {selectedFile && (
              <div className="mt-4 flex space-x-2">
                <button
                  className={`flex-1 px-3 py-2 rounded-lg font-medium transition-colors ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}
                  onClick={clearAll}
                  type="button"
                >
                  🗑️ Clear All
                </button>
                <button
                  className={`flex-1 px-3 py-2 rounded-lg font-medium transition-colors ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}
                  onClick={() => {
                    setOutputFormat('json-pretty');
                    setStructureMode('automatic');
                    setQualityLevel('balanced');
                    track('settings_reset');
                  }}
                  type="button"
                >
                  🔄 Reset Settings
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Information Section */}
        <div className={`mt-8 p-6 rounded-lg shadow-sm border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>About PDF to JSON Conversion</h2>

          <div className="space-y-4">
            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>How This Converter Works</h3>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                This advanced PDF to JSON converter uses sophisticated algorithms to analyze PDF document structure, extract content,
                and convert it into well-structured JSON format. The tool supports multiple output formats including standard JSON,
                JSON Lines (JSONL), and schema-validated JSON, making it perfect for API integration, data analysis, and automated workflows.
              </p>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Structure Modes</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li><strong>Automatic Detection</strong> - Intelligently determines the best structure based on document content</li>
                <li><strong>Page-Based Structure</strong> - Organizes content by pages with nested elements and positioning</li>
                <li><strong>Table-Based Extraction</strong> - Focuses on extracting and structuring tabular data</li>
                <li><strong>Text Block Analysis</strong> - Groups content into logical text blocks and sections</li>
                <li><strong>Hierarchical Structure</strong> - Creates nested JSON reflecting document hierarchy</li>
                <li><strong>Flat Array Format</strong> - Simple array structure for easy processing and analysis</li>
              </ul>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Output Formats</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li><strong>JSON (Pretty)</strong> - Human-readable JSON with proper indentation and formatting</li>
                <li><strong>JSON (Compact)</strong> - Minified JSON for reduced file size and faster processing</li>
                <li><strong>JSON Lines (.jsonl)</strong> - Each line is a separate JSON object, ideal for streaming</li>
                <li><strong>JSON with Schema</strong> - Includes JSON schema definition for validation</li>
              </ul>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Advanced Features</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li><strong>Table Recognition</strong> - Advanced algorithms to detect and extract tabular data</li>
                <li><strong>OCR Support</strong> - Extract text from scanned PDFs and image-based documents</li>
                <li><strong>Metadata Extraction</strong> - Include document properties, creation dates, and author information</li>
                <li><strong>Image Handling</strong> - Options to ignore, extract metadata, or embed images as base64</li>
                <li><strong>Data Type Detection</strong> - Automatic recognition of numbers, dates, and boolean values</li>
                <li><strong>Custom Formatting</strong> - Configurable JSON structure, indentation, and data types</li>
                <li><strong>Position Information</strong> - Include coordinates and positioning data for elements</li>
                <li><strong>Text Cleanup</strong> - Remove unwanted characters and normalize text content</li>
                <li><strong>Watermark Removal</strong> - Attempt to filter out watermarks and background elements</li>
                <li><strong>Multi-language OCR</strong> - Support for 13 languages including English, Chinese, and Arabic</li>
              </ul>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Common Use Cases</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li><strong>API Integration</strong> - Convert PDF reports to JSON for consumption by web services</li>
                <li><strong>Data Analysis</strong> - Extract structured data from PDFs for statistical analysis</li>
                <li><strong>Document Processing</strong> - Automate extraction of information from forms and reports</li>
                <li><strong>Content Migration</strong> - Transfer PDF content to databases or content management systems</li>
                <li><strong>Machine Learning</strong> - Prepare training data from PDF documents</li>
                <li><strong>Invoice Processing</strong> - Extract billing information for accounting automation</li>
                <li><strong>Research Data</strong> - Convert academic papers and research data to structured format</li>
                <li><strong>Legal Documents</strong> - Extract clauses, terms, and structured information from contracts</li>
              </ul>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>JSON Structure Examples</h3>
              <div className={`space-y-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <div>
                  <strong>Page-Based Structure:</strong>
                  <pre className={`mt-1 p-2 text-xs rounded ${isDark ? 'bg-gray-900 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
{`{
  "document": {
    "metadata": { "title": "Report", "pageCount": 3 },
    "pages": [
      {
        "pageNumber": 1,
        "content": [
          { "type": "heading", "text": "Title", "position": { "x": 100, "y": 50 } },
          { "type": "table", "data": [...], "position": { "x": 50, "y": 100 } }
        ]
      }
    ]
  }
}`}
                  </pre>
                </div>
                
                <div>
                  <strong>Table-Based Structure:</strong>
                  <pre className={`mt-1 p-2 text-xs rounded ${isDark ? 'bg-gray-900 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
{`{
  "tables": [
    {
      "tableId": 1,
      "page": 1,
      "headers": ["Product", "Sales", "Revenue"],
      "rows": [
        ["Widget A", 1500, 75000],
        ["Widget B", 1200, 60000]
      ]
    }
  ]
}`}
                  </pre>
                </div>
              </div>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Best Practices</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li>Use high-quality PDFs with clear structure for best extraction results</li>
                <li>Enable OCR for scanned documents or when text appears as images</li>
                <li>Choose appropriate structure mode based on your data processing needs</li>
                <li>Include metadata when you need document properties and creation information</li>
                <li>Use flat array format for simple data processing and analysis workflows</li>
                <li>Enable table extraction for documents with tabular data</li>
                <li>Adjust image settings for poorly scanned or low-quality documents</li>
                <li>Test with a small page range first for large or complex documents</li>
              </ul>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Technical Specifications</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li><strong>Input:</strong> PDF files up to 100MB in size</li>
                <li><strong>Output:</strong> JSON, JSONL, and JSON with schema formats</li>
                <li><strong>Processing:</strong> Client-side conversion for privacy and security</li>
                <li><strong>Encoding:</strong> UTF-8, UTF-16, and other character encodings</li>
                <li><strong>Data Types:</strong> Automatic detection of strings, numbers, dates, and booleans</li>
                <li><strong>Structure:</strong> Configurable JSON hierarchy and organization</li>
                <li><strong>Performance:</strong> Multi-threaded processing for faster conversion</li>
                <li><strong>Compatibility:</strong> Works with all modern browsers and operating systems</li>
              </ul>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Data Privacy & Security</h3>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                All PDF processing is performed entirely in your browser using client-side JavaScript. Your files are never uploaded
                to external servers, ensuring complete privacy and security. The conversion happens locally on your device, and no
                data is transmitted or stored remotely. This approach guarantees that your sensitive documents remain confidential
                while providing fast, reliable PDF to JSON conversion.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Wrapper component with ThemeProvider
const PDFToJSON: React.FC = () => {
  return (
    <ThemeProvider>
      <PDFToJSONContent />
    </ThemeProvider>
  );
};

export default PDFToJSON;
               